import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "../client";
import {
  BreakdownResponse,
  ShotCreate,
  ShotRead,
  ShotUpdate,
  type ShotCreate as ShotCreateT,
  type ShotUpdate as ShotUpdateT,
} from "../schemas";
import { qk } from "../queryKeys";

const ShotList = z.array(ShotRead);

export function useShots(sceneId: number) {
  // When a breakdown mutation is in flight for THIS scene, poll every 1.5s so
  // the UI picks up progressively-committed shots without manual refresh.
  const breakdownPending = useIsMutating({ mutationKey: qk.mk.breakdown(sceneId) });
  return useQuery({
    queryKey: qk.shots(sceneId),
    queryFn: () => api.get(`/api/scenes/${sceneId}/shots`, ShotList),
    enabled: sceneId > 0,
    refetchInterval: breakdownPending > 0 ? 1500 : false,
  });
}

/**
 * POST /api/scenes/{scene_id}/shots — create a new blank shot.
 *
 * On success we append the new shot to the scene's shots cache and then
 * invalidate so ordinals are reconciled from the server.
 */
export function useCreateShot(sceneId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ShotCreateT) =>
      api.post(`/api/scenes/${sceneId}/shots`, ShotCreate.parse(input), ShotRead),
    onSuccess: (created) => {
      qc.setQueryData<ShotRead[]>(qk.shots(sceneId), (prev) =>
        prev ? [...prev, created] : [created],
      );
      qc.invalidateQueries({ queryKey: qk.shots(sceneId) });
    },
  });
}

/**
 * PATCH /api/shots/{id} — partial update of a shot.
 *
 * On success we splice the returned row into the scene's shots cache so the
 * UI updates without a round-trip refetch, then invalidate to reconcile with
 * the server (covers the case where ordinal changes resorted the list).
 *
 * Caller is responsible for providing the parent `sceneId` so we know which
 * cached list to patch — the shot itself carries it (`shot.scene_id`) but the
 * caller already has it in scope.
 */
export function useUpdateShot(sceneId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: ShotUpdateT }) =>
      api.patch(`/api/shots/${id}`, ShotUpdate.parse(patch), ShotRead),
    onSuccess: (updated) => {
      qc.setQueryData<ShotRead[]>(qk.shots(sceneId), (prev) =>
        prev ? prev.map((s) => (s.id === updated.id ? updated : s)) : prev,
      );
      qc.invalidateQueries({ queryKey: qk.shots(sceneId) });
    },
  });
}

/**
 * POST /api/scenes/{id}/breakdown — LLM-backed; expect 5–30s latency.
 * On success the backend has created N new Shots and returns them; we both
 * write the response into the shots-list cache and invalidate, so the UI
 * paints immediately and the next refetch confirms order.
 */
export function useRunBreakdown(sceneId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: qk.mk.breakdown(sceneId),
    mutationFn: () =>
      api.postEmpty(`/api/scenes/${sceneId}/breakdown`, BreakdownResponse),
    onSuccess: (result) => {
      qc.setQueryData(qk.shots(sceneId), result.shots);
      qc.invalidateQueries({ queryKey: qk.shots(sceneId) });
    },
  });
}

/**
 * POST /api/shots/{shot_id}/generate-prompt — user-triggered LLM prompt
 * generation for a single shot. Accepts an optional `instructions` string
 * that steers the rewrite as the highest-priority direction.
 *
 * On success we patch the returned shot directly into the scene's shots cache
 * so the UI updates without a full refetch round-trip.
 */
export function useGeneratePrompt(sceneId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shotId, instructions }: { shotId: number; instructions?: string }) =>
      api.post(
        `/api/shots/${shotId}/generate-prompt`,
        { instructions: instructions ?? null },
        ShotRead,
      ),
    onSuccess: (updated) => {
      qc.setQueryData<ShotRead[]>(qk.shots(sceneId), (prev) =>
        prev ? prev.map((s) => (s.id === updated.id ? updated : s)) : prev,
      );
      qc.invalidateQueries({ queryKey: qk.shots(sceneId) });
    },
  });
}

/**
 * DELETE /api/shots/{shot_id} — removes a shot from its parent scene.
 *
 * Backend route is flat (matching useUpdateShot's PATCH /api/shots/{id}); the
 * scene_id is only needed here so we know which cached shots list to update
 * optimistically. Returns 204 No Content on success.
 *
 * Optimistic update: we immediately drop the row from the cached shots array
 * so the card disappears under the user's finger. On error we restore the
 * snapshot — a 404 means someone else already deleted it (still feels
 * correct), other failures roll back so the UI stays honest. onSettled
 * always invalidates to reconcile with the server.
 */
export function useDeleteShot(sceneId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shotId: number) => api.delete(`/api/shots/${shotId}`),
    onMutate: async (shotId: number) => {
      // Cancel in-flight refetches so they don't clobber our optimistic write.
      await qc.cancelQueries({ queryKey: qk.shots(sceneId) });
      const previous = qc.getQueryData<ShotRead[]>(qk.shots(sceneId));
      qc.setQueryData<ShotRead[]>(qk.shots(sceneId), (prev) =>
        prev ? prev.filter((s) => s.id !== shotId) : prev,
      );
      // Returned context flows through to onError as the second argument.
      return { previous };
    },
    onError: (_err, _shotId, context) => {
      if (context?.previous) {
        qc.setQueryData(qk.shots(sceneId), context.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.shots(sceneId) });
    },
  });
}
